import { copyNow } from '../i18n/vtoCopy';
import type { LipDetection } from '../tracking/faceLandmarker';

export type SkinDepthClass = 'very-light' | 'light' | 'medium' | 'tan' | 'deep';
export type SkinChromaClass = 'muted' | 'balanced' | 'rich';

export interface SkinToneCalibrationTuning {
  skinStrengthScale: number;
  cheekOpacityScale: number;
  eyeOpacityScale: number;
  eyeSparkleScale: number;
  lipOpacityScale: number;
  lipFinishScale: number;
}

export interface SkinToneCalibrationReport {
  depth: SkinDepthClass;
  chroma: SkinChromaClass;
  confidence: number;
  ita: number;
  lightness: number;
  labA: number;
  labB: number;
  sampleCount: number;
  tuning: SkinToneCalibrationTuning;
  warnings: string[];
}

const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
  361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

// Tracciati separati per occhio sinistro e destro: un singolo array unito
// produce un poligono che attraversa il dorsale del naso, escludendo
// erroneamente pelle valida dal sample. Vanno passati a tracePath uno alla
// volta dentro lo stesso beginPath() per ottenere due "fori" indipendenti
// con fill('evenodd').
const EXCLUDE_LEFT_EYE = [
  33, 246, 161, 160, 159, 158, 157, 173, 133,
  155, 154, 153, 145, 144, 163, 7
];

const EXCLUDE_RIGHT_EYE = [
  263, 466, 388, 387, 386, 385, 384, 398, 362,
  382, 381, 380, 374, 373, 390, 249
];

const EXCLUDE_MOUTH = [
  61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291,
  375, 321, 405, 314, 17, 84, 181, 91, 146
];

const EXCLUDE_BROWS = [
  70, 63, 105, 66, 107,
  336, 296, 334, 293, 300
];

const DEFAULT_TUNING: SkinToneCalibrationTuning = {
  skinStrengthScale: 1,
  cheekOpacityScale: 1,
  eyeOpacityScale: 1,
  eyeSparkleScale: 1,
  lipOpacityScale: 1,
  lipFinishScale: 1
};

interface LabSample {
  l: number;
  a: number;
  b: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function tracePath(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[],
  width: number,
  height: number
): void {
  let started = false;
  for (const idx of indices) {
    const p = detection.landmarks[idx];
    if (!p) continue;
    const x = p.x * width;
    const y = p.y * height;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  if (started) ctx.closePath();
}

function srgbToLinear(value: number): number {
  const normalized = value / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

function xyzPivot(value: number): number {
  return value > 0.008856 ? Math.cbrt(value) : (7.787 * value) + (16 / 116);
}

function rgbToLab(r: number, g: number, b: number): LabSample {
  const rl = srgbToLinear(r);
  const gl = srgbToLinear(g);
  const bl = srgbToLinear(b);

  const x = (0.4124564 * rl + 0.3575761 * gl + 0.1804375 * bl) / 0.95047;
  const y = (0.2126729 * rl + 0.7151522 * gl + 0.0721750 * bl) / 1.00000;
  const z = (0.0193339 * rl + 0.1191920 * gl + 0.9503041 * bl) / 1.08883;

  const fx = xyzPivot(x);
  const fy = xyzPivot(y);
  const fz = xyzPivot(z);

  return {
    l: (116 * fy) - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz)
  };
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

function sampleSkinLab(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  size = 112
): { l: number; a: number; b: number; samples: number } | null {
  const image = document.createElement('canvas');
  image.width = size;
  image.height = size;
  const ictx = image.getContext('2d', { willReadFrequently: true });
  if (!ictx) return null;
  ictx.drawImage(source, 0, 0, size, size);

  const mask = document.createElement('canvas');
  mask.width = size;
  mask.height = size;
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return null;

  mctx.fillStyle = '#ffffff';
  mctx.beginPath();
  tracePath(mctx, detection, FACE_OVAL, size, size);
  tracePath(mctx, detection, EXCLUDE_LEFT_EYE, size, size);
  tracePath(mctx, detection, EXCLUDE_RIGHT_EYE, size, size);
  tracePath(mctx, detection, EXCLUDE_MOUTH, size, size);
  // Brow-protect dischi: a size=112 il valore precedente (0.014 → 1.57 px)
  // copriva una frazione dell'area sopracciglio reale (≈10×3 px), lasciando
  // l'80% dei pixel-sopracciglio nel sample skin e tirando la mediana di
  // L*/b* verso il marrone/scuro. 0.05 → 5.6 px di raggio copre il bulk
  // del sopracciglio senza invadere significativamente la pelle adiacente.
  for (const idx of EXCLUDE_BROWS) {
    const p = detection.landmarks[idx];
    if (!p) continue;
    const radius = size * 0.05;
    mctx.moveTo(p.x * size + radius, p.y * size);
    mctx.arc(p.x * size, p.y * size, radius, 0, Math.PI * 2);
  }
  mctx.fill('evenodd');

  const pixels = ictx.getImageData(0, 0, size, size).data;
  const maskPixels = mctx.getImageData(0, 0, size, size).data;
  const lValues: number[] = [];
  const aValues: number[] = [];
  const bValues: number[] = [];

  for (let i = 0; i < pixels.length; i += 4) {
    if ((maskPixels[i] ?? 0) < 200) continue;
    const r = pixels[i] ?? 0;
    const g = pixels[i + 1] ?? 0;
    const b = pixels[i + 2] ?? 0;
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    if (lum < 0.09 || lum > 0.96) continue;
    const lab = rgbToLab(r, g, b);
    lValues.push(lab.l);
    aValues.push(lab.a);
    bValues.push(lab.b);
  }

  if (lValues.length < 150) return null;
  return {
    l: median(lValues),
    a: median(aValues),
    b: median(bValues),
    samples: lValues.length
  };
}

function classifyDepth(ita: number): SkinDepthClass {
  if (ita > 55) return 'very-light';
  if (ita > 41) return 'light';
  if (ita > 28) return 'medium';
  if (ita > 10) return 'tan';
  return 'deep';
}

function classifyChroma(labA: number, labB: number): SkinChromaClass {
  const chroma = Math.hypot(labA, labB);
  if (chroma < 16) return 'muted';
  if (chroma > 27) return 'rich';
  return 'balanced';
}

function tuningFor(depth: SkinDepthClass, chroma: SkinChromaClass): SkinToneCalibrationTuning {
  const tuning: SkinToneCalibrationTuning = { ...DEFAULT_TUNING };

  if (depth === 'very-light') {
    tuning.skinStrengthScale *= 0.94;
    tuning.cheekOpacityScale *= 0.9;
    tuning.eyeOpacityScale *= 0.92;
    tuning.eyeSparkleScale *= 0.9;
    tuning.lipOpacityScale *= 0.94;
    tuning.lipFinishScale *= 0.96;
  } else if (depth === 'light') {
    tuning.skinStrengthScale *= 0.97;
    tuning.cheekOpacityScale *= 0.96;
    tuning.eyeOpacityScale *= 0.97;
    tuning.eyeSparkleScale *= 0.96;
    tuning.lipOpacityScale *= 0.98;
  } else if (depth === 'tan') {
    tuning.skinStrengthScale *= 0.96;
    tuning.cheekOpacityScale *= 1.07;
    tuning.eyeOpacityScale *= 1.08;
    tuning.eyeSparkleScale *= 1.03;
    tuning.lipOpacityScale *= 1.04;
    tuning.lipFinishScale *= 0.98;
  } else if (depth === 'deep') {
    tuning.skinStrengthScale *= 0.93;
    tuning.cheekOpacityScale *= 1.15;
    tuning.eyeOpacityScale *= 1.14;
    tuning.eyeSparkleScale *= 1.05;
    tuning.lipOpacityScale *= 1.08;
    tuning.lipFinishScale *= 0.96;
  }

  if (chroma === 'muted') {
    tuning.cheekOpacityScale *= 0.96;
    tuning.eyeSparkleScale *= 0.94;
    tuning.lipFinishScale *= 0.96;
  } else if (chroma === 'rich') {
    tuning.cheekOpacityScale *= 1.03;
    tuning.eyeOpacityScale *= 1.02;
    tuning.lipOpacityScale *= 1.02;
  }

  tuning.skinStrengthScale = clamp(tuning.skinStrengthScale, 0.88, 1.02);
  tuning.cheekOpacityScale = clamp(tuning.cheekOpacityScale, 0.86, 1.18);
  tuning.eyeOpacityScale = clamp(tuning.eyeOpacityScale, 0.88, 1.16);
  tuning.eyeSparkleScale = clamp(tuning.eyeSparkleScale, 0.86, 1.06);
  tuning.lipOpacityScale = clamp(tuning.lipOpacityScale, 0.9, 1.1);
  tuning.lipFinishScale = clamp(tuning.lipFinishScale, 0.92, 1.02);

  return tuning;
}

export function analyzeSkinToneCalibration(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection
): SkinToneCalibrationReport {
  const scopy = copyNow().skinTone;
  const sample = sampleSkinLab(source, detection);
  const warnings: string[] = [];

  if (!sample) {
    return {
      depth: 'medium',
      chroma: 'balanced',
      confidence: 0,
      ita: 35,
      lightness: 0,
      labA: 0,
      labB: 0,
      sampleCount: 0,
      tuning: { ...DEFAULT_TUNING },
      warnings: [scopy.unmeasurable]
    };
  }

  // Individual Typology Angle (Chardon 1991): atan((L*-50)/b*) in gradi.
  // atan2 reagisce al segno di b* cambiando quadrante e produce
  // classificazioni saltellanti su pelli molto fredde / illuminazione blu;
  // atan sul rapporto è il calcolo canonico. b* è praticamente sempre > 0
  // su pelle umana, quindi un guard minimo evita la divisione per zero.
  const ita = Math.atan((sample.l - 50) / (sample.b !== 0 ? sample.b : 1e-6))
    * (180 / Math.PI);
  const depth = classifyDepth(ita);
  const chroma = classifyChroma(sample.a, sample.b);
  const confidence = clamp(sample.samples / (112 * 112 * 0.2), 0, 1);

  if (confidence < 0.35) {
    warnings.push(scopy.reducedSample);
  }

  return {
    depth,
    chroma,
    confidence,
    ita,
    lightness: sample.l,
    labA: sample.a,
    labB: sample.b,
    sampleCount: sample.samples,
    tuning: confidence < 0.2 ? { ...DEFAULT_TUNING } : tuningFor(depth, chroma),
    warnings
  };
}
